import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { PackEntry } from 'types/shared';

const DEFAULT_APP = 'YG';
const DEFAULT_NETWORK = 'Facebook';

interface UseTestCreativesViewStateArgs {
  defaultApp?: string;
}

export interface UpdateSelectionOptions {
  syncActivePack?: boolean;
}

interface UseTestCreativesViewStateResult {
  app: string;
  network: string;
  selectedCreatives: string[];
  updateSelection: (ids: string[], options?: UpdateSelectionOptions) => void;
}

const useOptionalFormContext = (): UseFormReturn | null => {
  try {
    return useFormContext();
  } catch {
    return null;
  }
};

const useTestCreativesViewState = ({
  defaultApp,
}: UseTestCreativesViewStateArgs = {}): UseTestCreativesViewStateResult => {
  const formContext = useOptionalFormContext();
  const hasForm = !!formContext;

  const app =
    (hasForm ? (formContext.watch('app') as string) : null) ??
    defaultApp ??
    DEFAULT_APP;
  const network =
    (hasForm ? (formContext.watch('network') as string) : null) ?? DEFAULT_NETWORK;

  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const selectedCreatives = hasForm
    ? (formContext.watch('selectedCreatives') as string[])
    : localSelected;

  const formContextRef = useRef(formContext);
  useEffect(() => {
    formContextRef.current = formContext;
  }, [formContext]);

  const updateSelection = useCallback(
    (ids: string[], options?: UpdateSelectionOptions) => {
      const ctx = formContextRef.current;
      if (ctx) {
        ctx.setValue('selectedCreatives', ids);
        const syncActivePack = options?.syncActivePack ?? true;
        if (!syncActivePack) return;
        const activeIndex = ctx.getValues('activePacksWithCreatives') as
          | number
          | null;
        if (activeIndex !== null) {
          const currentPacks = ctx.getValues('packsWithCreatives') as PackEntry[];
          ctx.setValue(
            'packsWithCreatives',
            currentPacks.map((pack, index) =>
              index === activeIndex ? { ...pack, creatives: ids } : pack,
            ),
          );
        }
      } else {
        setLocalSelected(ids);
      }
    },
    [],
  );

  return {
    app,
    network,
    selectedCreatives,
    updateSelection,
  };
};

export default useTestCreativesViewState;
